import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { initCpSizePicker } from '@theme/cp-size-init';

/**
 * Desktop : hauteur modale quick-add = contenu colonne infos (image carrée).
 * @param {Element} modalContent
 */
function sizeQuickAddModal(modalContent) {
  const dialog = document.getElementById('quick-add-dialog')?.querySelector('.quick-add-modal');
  if (!(dialog instanceof HTMLElement)) return;

  if (!window.matchMedia('(min-width: 750px)').matches) {
    dialog.style.removeProperty('--quick-add-detail-size');
    return;
  }

  const details = modalContent.querySelector('.product-details');
  const buyButtons = modalContent.querySelector('.buy-buttons-block');
  const content = details?.querySelector('.group-block-content');
  if (!(content instanceof HTMLElement)) return;

  const needed = content.scrollHeight + (buyButtons instanceof HTMLElement ? buyButtons.offsetHeight : 0);
  const floor = Math.min(window.innerHeight * 0.55, 520);
  const maxSquare = Math.min(window.innerHeight * 0.64, 600);
  const size = Math.round(Math.max(floor, Math.min(needed, maxSquare)));
  dialog.style.setProperty('--quick-add-detail-size', `${size}px`);
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    morph(modalContent, productGrid);

    modalContent.classList.add('quick-add-modal__content--product-sheet');
    document.getElementById('quick-add-dialog')?.classList.add('quick-add-modal--product-sheet');
    this.#syncVariantSelection(modalContent);
    this.#scopeZoomDialogs(modalContent);
    this.#initQuickAddEnhancements(modalContent);
    this.#initQuickAddGallery(modalContent);
    requestAnimationFrame(() => requestAnimationFrame(() => sizeQuickAddModal(modalContent)));
  }

  /**
   * Le contenu de la modale est cloné depuis la page produit : ses zoom-dialog
   * gardent le MÊME id (#zoom-dialog-<block_id>) que celui de la fiche. Or le clic
   * de zoom résout l'id via document.querySelector(), qui renvoie le PREMIER du DOM
   * (celui de la fiche) → on ouvrait l'image de la page au lieu de celle de la modale.
   * On préfixe les ids de la modale et on recâble les on:click correspondants.
   * @param {Element} modalContent
   */
  #scopeZoomDialogs(modalContent) {
    const prefix = 'qa-';
    modalContent.querySelectorAll('zoom-dialog[id^="zoom-dialog-"]').forEach((dialog) => {
      const oldId = dialog.id;
      if (oldId.startsWith(prefix)) return;
      const newId = prefix + oldId;
      dialog.id = newId;

      modalContent.querySelectorAll(`[on\\:click^="#${oldId}/"]`).forEach((el) => {
        const value = el.getAttribute('on:click');
        if (value) el.setAttribute('on:click', value.replace(`#${oldId}/`, `#${newId}/`));
      });
    });
  }

  /**
   * Réactive la galerie après morph (1re slide visible, scroll horizontal).
   * @param {Element} modalContent
   */
  #initQuickAddGallery(modalContent) {
    const media = modalContent.querySelector('.product-information__media');
    if (!media) return;

    const slides = media.querySelectorAll('slideshow-slide');
    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      slide.removeAttribute('hidden');
    });

    const slidesContainer = media.querySelector('slideshow-slides');
    if (slidesContainer instanceof HTMLElement) {
      slidesContainer.scrollLeft = 0;
      this.#bindQuickAddGalleryScroll(slidesContainer, slides);
    }

    const slideshow = media.querySelector('slideshow-component');
    if (slideshow && typeof slideshow.select === 'function') {
      slideshow.select(0, undefined, { animate: false });
    }
  }

  /** @type {WeakMap<Element, AbortController>} */
  #quickAddGalleryControllers = new WeakMap();

  /**
   * Scroll horizontal desktop (molette / trackpad) + snap entre slides.
   * @param {HTMLElement} slidesContainer
   * @param {NodeListOf<Element>} slides
   */
  #bindQuickAddGalleryScroll(slidesContainer, slides) {
    this.#quickAddGalleryControllers.get(slidesContainer)?.abort();

    const controller = new AbortController();
    this.#quickAddGalleryControllers.set(slidesContainer, controller);
    const { signal } = controller;

    const syncActiveSlide = () => {
      if (!window.matchMedia('(min-width: 750px)').matches) return;
      const width = slidesContainer.clientWidth;
      if (!width) return;
      const index = Math.round(slidesContainer.scrollLeft / width);
      slides.forEach((slide, i) => {
        slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
      });
    };

    const snapToNearestSlide = () => {
      const width = slidesContainer.clientWidth;
      if (!width) return;
      const index = Math.round(slidesContainer.scrollLeft / width);
      slidesContainer.scrollTo({ left: index * width, behavior: 'smooth' });
      syncActiveSlide();
    };

    slidesContainer.addEventListener(
      'wheel',
      (event) => {
        if (!window.matchMedia('(min-width: 750px)').matches || !(event instanceof WheelEvent) || event.ctrlKey) {
          return;
        }

        const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
        const atStart = slidesContainer.scrollLeft <= 0;
        const atEnd = slidesContainer.scrollLeft + slidesContainer.clientWidth >= slidesContainer.scrollWidth - 1;

        if (!delta || (delta < 0 && atStart) || (delta > 0 && atEnd)) return;

        event.preventDefault();
        slidesContainer.scrollLeft += delta;
        syncActiveSlide();
      },
      { passive: false, signal }
    );

    slidesContainer.addEventListener('scroll', syncActiveSlide, { passive: true, signal });

    if ('onscrollend' in slidesContainer) {
      slidesContainer.addEventListener('scrollend', snapToNearestSlide, { passive: true, signal });
    } else {
      let snapTimer = /** @type {ReturnType<typeof setTimeout> | undefined} */ (undefined);
      slidesContainer.addEventListener(
        'scroll',
        () => {
          clearTimeout(snapTimer);
          snapTimer = setTimeout(snapToNearestSlide, 120);
        },
        { passive: true, signal }
      );
    }
  }

  /**
   * Accordéons visibles dans la modale (fiche allégée).
   * @type {string[]}
   */
  #quickAddAccordionHeadings = ['Description', 'Caractéristiques', 'Tissu'];

  /**
   * @param {string} heading
   * @returns {boolean}
   */
  #isQuickAddAccordionAllowed(heading) {
    const normalized = heading.replace(/\s+/g, ' ').trim();
    return this.#quickAddAccordionHeadings.some((label) => normalized.includes(label));
  }

  /**
   * Ne garde que Description / Caractéristiques / Tissu dans la modale.
   * @param {Element} modalContent
   */
  #trimQuickAddAccordions(modalContent) {
    modalContent.querySelectorAll('accordion-custom').forEach((acc) => {
      if (!(acc instanceof HTMLElement)) return;
      const summary = acc.querySelector('.details__header');
      const heading = summary?.textContent ?? '';
      if (!this.#isQuickAddAccordionAllowed(heading)) {
        acc.style.display = 'none';
        acc.setAttribute('aria-hidden', 'true');
      } else {
        acc.style.removeProperty('display');
        acc.removeAttribute('aria-hidden');
      }
    });
  }

  /**
   * Initialise cp-size et autres composants après injection dans la modale.
   * @param {Element} modalContent
   */
  #initQuickAddEnhancements(modalContent) {
    this.#trimQuickAddAccordions(modalContent);

    modalContent.querySelectorAll('dialog-component.cp-size').forEach((el) => {
      delete el.dataset.cpInit;
    });
    initCpSizePicker(modalContent);

    modalContent.querySelectorAll('accordion-custom details').forEach((details) => {
      if (!(details instanceof HTMLDetailsElement)) return;
      if (details.closest('accordion-custom')?.getAttribute('aria-hidden') === 'true') return;

      const heading = details.querySelector('.details__header')?.textContent ?? '';
      details.open = heading.includes('Description');
    });
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  #boundResize = () => this.#handleQuickAddResize();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    window.addEventListener('resize', this.#boundResize, { signal: this.#abortController.signal });
  }

  #handleQuickAddResize() {
    if (!this.classList.contains('quick-add-modal--product-sheet')) return;
    const dialog = this.refs?.dialog;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
    const modalContent = document.getElementById('quick-add-modal-content');
    if (modalContent) sizeQuickAddModal(modalContent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    document.getElementById('quick-add-dialog')?.classList.remove('quick-add-modal--product-sheet');
    document.getElementById('quick-add-modal-content')?.classList.remove('quick-add-modal__content--product-sheet');
    document
      .getElementById('quick-add-dialog')
      ?.querySelector('.quick-add-modal')
      ?.style.removeProperty('--quick-add-detail-size');

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
